'use client'

import clsx from 'clsx'
import { getImageProps } from 'next/image'
import { useFormatter, useTranslations } from 'next-intl'

import ChevronRightIcon from '@/assets/icons/chevron-right.svg'
import ApIcon from '@/assets/icons/tasks-feed/ap.svg'
import ClockIcon from '@/assets/icons/tasks-feed/clock.svg'
import UsersIcon from '@/assets/icons/tasks-feed/users.svg'
import { formatTaskHoursLeft } from '@/features/tasks/formatTaskHoursLeft'
import type { ResolvedTaskBanner } from '@/features/tasks/taskBanners'
import { Link } from '@/i18n/routing'
import { useIsHydrated } from '@/lib/datetime/useIsHydrated'
import Image from '@/ui/MediaImage'
import { toMediaCdnUrl } from '@/utils/imageUrl'

import { formatParticipantCap } from '../TaskCard/taskCard.utils'
import S from './TasksHeroBanners.module.scss'

type TaskBannerSlideProps = {
  banner: ResolvedTaskBanner
  /** The first slide is the LCP image. */
  eager: boolean
}

const TaskBannerSlide = ({ banner, eager }: TaskBannerSlideProps) => {
  const t = useTranslations('tasksPage.heroBanners')
  const tTime = useTranslations('tasksPage.activeSection')
  const format = useFormatter()
  // Time left reads Date.now(); render it after hydration only.
  const isHydrated = useIsHydrated()

  const { props: desktopImage } = getImageProps({
    alt: '',
    fill: true,
    sizes: '(max-width: 768px) 100vw, min(67vw, 964px)',
    src: toMediaCdnUrl(banner.desktopImageUrl),
    loading: eager ? 'eager' : 'lazy',
    fetchPriority: eager ? 'high' : undefined
  })
  const {
    props: { srcSet: mobileSrcSet }
  } = getImageProps({
    alt: '',
    fill: true,
    sizes: '100vw',
    src: toMediaCdnUrl(banner.mobileImageUrl)
  })

  const note =
    banner.note ??
    (banner.campaignTaskCount === undefined
      ? undefined
      : t('campaignTasks', { count: banner.campaignTaskCount }))

  const sponsor = (
    <>
      {banner.sponsorLogoUrl && (
        <span className={S.sponsorLogo}>
          <Image src={banner.sponsorLogoUrl} alt="" fill sizes="32px" />
        </span>
      )}
      {banner.sponsorName && <span>{banner.sponsorName}</span>}
    </>
  )

  const ctaContent = (
    <>
      <span>{banner.ctaText ?? t('viewTask')}</span>
      <ChevronRightIcon className={S.ctaIcon} aria-hidden />
    </>
  )

  return (
    <div className={S.slide}>
      <picture className={S.media}>
        <source media="(max-width: 768px)" srcSet={mobileSrcSet} />
        <img {...desktopImage} alt="" className={S.image} />
      </picture>

      <div className={S.content}>
        {(banner.sponsorName || banner.sponsorLogoUrl) &&
          (banner.sponsorUrl ? (
            <a
              className={clsx(S.sponsor, S.sponsorLink)}
              href={banner.sponsorUrl}
              aria-label={
                banner.sponsorName
                  ? undefined
                  : new URL(banner.sponsorUrl).hostname
              }
              target="_blank"
              rel="noopener noreferrer"
            >
              {sponsor}
            </a>
          ) : (
            <div className={S.sponsor}>{sponsor}</div>
          ))}

        <h2 className={S.title}>{banner.title}</h2>

        {banner.description && (
          <p className={S.description}>{banner.description}</p>
        )}
        {banner.task && (
          <ul className={S.stats}>
            <li>
              <ApIcon className={S.statIcon} aria-hidden />
              {t('reward', { points: format.number(banner.task.rewardPoints) })}
            </li>
            <li>
              <UsersIcon className={S.statIcon} aria-hidden />
              {t('participants', {
                current: formatParticipantCap(
                  banner.task.participantCurrent
                ).toLowerCase(),
                limit: formatParticipantCap(
                  banner.task.participantLimit
                ).toLowerCase()
              })}
            </li>
            {isHydrated && (
              <li>
                <ClockIcon className={S.statIcon} aria-hidden />
                {t('timeLeft', {
                  time: formatTaskHoursLeft(banner.task.hoursLeft, tTime)
                })}
              </li>
            )}
          </ul>
        )}

        {(banner.href || note) && (
          <div className={S.ctaRow}>
            {banner.href &&
              (banner.external ? (
                <a
                  className={S.cta}
                  href={banner.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {ctaContent}
                </a>
              ) : (
                <Link className={S.cta} href={banner.href}>
                  {ctaContent}
                </Link>
              ))}
            {note && <span className={S.note}>{note}</span>}
          </div>
        )}
      </div>
    </div>
  )
}

export default TaskBannerSlide
